import { isFunction } from 'lodash-es';
import { ComponentContext } from './schemas';

export function isComponentValueCache(
  target: any | null,
): target is ComponentValueCache {
  return target instanceof ComponentValueCache;
}

/**
 * Helper class to cache component values.
 */
export class ComponentValueCache {
  /**
   * The attribute name on the component.
   */
  readonly attr: string;
  /**
   * The component instance this cache hase been attached to.
   */
  readonly component: ComponentContext;
  /**
   * The unique storage key name,
   */
  readonly storageKey: string | Function;

  /**
   * The unwatch unsubscriber function.
   */
  private unwatch?: Function;

  constructor(
    component: ComponentContext,
    attr: string,
    storageKey: string | Function,
  ) {
    this.attr = attr;
    this.component = component;
    this.storageKey = storageKey;

    this.registerWatcher();
  }

  get effectiveStorageKey() {
    return isFunction(this.storageKey)
      ? this.storageKey.call(this.component)
      : this.storageKey;
  }

  /**
   * Read current value from storage.
   */
  getValue() {
    return this.component.$app.objectStore.get(this.effectiveStorageKey);
  }

  private registerWatcher() {
    this.unwatch = this.component.$watch(
      this.attr,
      (v: any, o: any) => this.onValueChange(v, o),
      {
        deep: true,
      },
    );
  }

  public unsubscribeWatcher() {
    if (this.unwatch) {
      this.unwatch();
      this.unwatch = undefined;
    }
  }

  private onValueChange(value: any, oldValue: any) {
    this.persist(value);
  }

  async persist(value: any) {
    const k = this.effectiveStorageKey;
    if (k != null) {
      this.component.$app.objectStore.set(this.effectiveStorageKey, value);
    }
  }

  async clearCache() {
    this.component.$app.objectStore.set(this.effectiveStorageKey, null);
  }
}
